// src/services/calendarioService.js
import { pb } from "./pocketbase";

/**
 * Busca os feriados e dias não úteis cadastrados no PocketBase
 * @param {number|string} [ano] - Ano opcional para filtragem
 */
export async function getFeriados(ano = null) {
  try {
    const filter = ano ? `ano = ${Number(ano)}` : "";
    return await pb.collection("oficina_calendario_feriados").getFullList({
      filter,
      sort: "+data",
    });
  } catch (err) {
    console.error("Erro ao buscar oficina_calendario_feriados:", err);
    return [];
  }
}

/**
 * Cria um novo dia não útil no calendário
 */
export async function createFeriado(data) {
  const payload = {
    data: String(data.data || "").trim(),
    descricao: String(data.descricao || "").trim(),
    tipo: data.tipo || "personalizado",
    ano: Number(data.ano) || new Date(data.data).getFullYear(),
    ativo: data.ativo !== false,
  };
  return await pb.collection("oficina_calendario_feriados").create(payload);
}

/**
 * Atualiza um feriado existente
 */
export async function saveFeriado(id, data) {
  return await pb.collection("oficina_calendario_feriados").update(id, data);
}

/**
 * Ativa ou desativa um feriado
 */
export async function toggleFeriado(id, ativo) {
  return await pb.collection("oficina_calendario_feriados").update(id, { ativo: Boolean(ativo) });
}

/**
 * Deleta um dia do calendário
 */
export async function deleteFeriado(id) {
  return await pb.collection("oficina_calendario_feriados").delete(id);
}

/**
 * Algoritmo matemático canônico para calcular a data do Domingo de Páscoa (Computus de Meeus)
 * @param {number} year 
 * @returns {Date} Data do Domingo de Páscoa (ao meio-dia UTC)
 */
export function calcularPascoa(year) {
  const y = Number(year);
  const a = y % 19;
  const b = Math.floor(y / 100);
  const c = y % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3 = Março, 4 = Abril
  const day = ((h + l - 7 * m + 114) % 31) + 1;

  return new Date(Date.UTC(y, month - 1, day, 12, 0, 0));
}

/**
 * Formata um objeto Date para string ISO "YYYY-MM-DD"
 */
export function dateToIso(d) {
  if (!d) return "";
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Gera lista de feriados com base em cálculos matemáticos caso a API esteja offline
 */
export function calcularFeriadosFallback(ano) {
  const y = Number(ano);
  const pascoa = calcularPascoa(y);

  const addDays = (base, days) => {
    const res = new Date(base.getTime());
    res.setUTCDate(res.getUTCDate() + days);
    return res;
  };

  const sextaSanta = addDays(pascoa, -2);
  const carnavalTer = addDays(pascoa, -47);
  const carnavalSeg = addDays(pascoa, -48);
  const corpusChristi = addDays(pascoa, 60);

  return [
    { data: `${y}-01-01`, descricao: "Confraternização Universal (Ano Novo)", tipo: "nacional" },
    { data: `${y}-01-25`, descricao: "Aniversário de São Paulo", tipo: "municipal" },
    { data: dateToIso(carnavalSeg), descricao: "Carnaval (Segunda-feira)", tipo: "facultativo" },
    { data: dateToIso(carnavalTer), descricao: "Carnaval (Terça-feira)", tipo: "facultativo" },
    { data: dateToIso(sextaSanta), descricao: "Sexta-feira Santa (Paixão de Cristo)", tipo: "nacional" },
    { data: `${y}-04-21`, descricao: "Tiradentes", tipo: "nacional" },
    { data: `${y}-05-01`, descricao: "Dia do Trabalhador", tipo: "nacional" },
    { data: dateToIso(corpusChristi), descricao: "Corpus Christi", tipo: "municipal" },
    { data: `${y}-07-09`, descricao: "Revolução Constitucionalista de 1932", tipo: "estadual" },
    { data: `${y}-09-07`, descricao: "Independência do Brasil", tipo: "nacional" },
    { data: `${y}-10-12`, descricao: "Nossa Senhora Aparecida (Padroeira do Brasil)", tipo: "nacional" },
    { data: `${y}-11-02`, descricao: "Finados", tipo: "nacional" },
    { data: `${y}-11-15`, descricao: "Proclamação da República", tipo: "nacional" },
    { data: `${y}-11-20`, descricao: "Dia da Consciência Negra", tipo: "nacional" },
    { data: `${y}-12-25`, descricao: "Natal", tipo: "nacional" },
  ];
}

/**
 * Busca sugestões de feriados para o ano informado combinando:
 * 1. BrasilAPI (Feriados Nacionais e Móveis)
 * 2. Feriados Estaduais de SP (09/07)
 * 3. Feriados Municipais de SP Capital (25/01 e Corpus Christi)
 * 4. Fallback matemático garantido caso a API falhe
 */
export async function buscarFeriadosSugeridos(ano) {
  const y = Number(ano) || new Date().getFullYear();
  let feriados = [];

  try {
    const res = await fetch(`https://brasilapi.com.br/api/feriados/v1/${y}`);
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        feriados = data.map((item) => {
          let tipo = "nacional";
          const descLower = String(item.name || "").toLowerCase();
          if (descLower.includes("carnaval")) tipo = "facultativo";
          if (descLower.includes("corpus")) tipo = "municipal"; // Corpus Christi é feriado municipal oficial em SP

          return {
            data: item.date,
            descricao: item.name,
            tipo,
          };
        });
      }
    }
  } catch (err) {
    console.warn("[calendarioService] BrasilAPI offline ou inacessível, usando fallback de cálculo:", err);
  }

  // Se a API não retornou ou falhou, usa o fallback matemático completo
  if (feriados.length === 0) {
    feriados = calcularFeriadosFallback(y);
  }

  // Garante a inclusão dos feriados específicos de São Paulo Capital se não estiverem na lista
  const datasSet = new Set(feriados.map((f) => f.data));

  // 25/01 - Aniversário de São Paulo (Municipal)
  const dataAnivSP = `${y}-01-25`;
  if (!datasSet.has(dataAnivSP)) {
    feriados.push({
      data: dataAnivSP,
      descricao: "Aniversário de São Paulo",
      tipo: "municipal",
    });
    datasSet.add(dataAnivSP);
  }

  // 09/07 - Revolução Constitucionalista de 1932 (Estadual SP)
  const dataRevSP = `${y}-07-09`;
  if (!datasSet.has(dataRevSP)) {
    feriados.push({
      data: dataRevSP,
      descricao: "Revolução Constitucionalista de 1932",
      tipo: "estadual",
    });
    datasSet.add(dataRevSP);
  }

  // Ordena cronologicamente por data
  feriados.sort((a, b) => a.data.localeCompare(b.data));

  // Adiciona flags padrão para sugestão (marcados por padrão, exceto pontos facultativos opcionais)
  return feriados.map((f) => ({
    ...f,
    ano: y,
    ativo: true,
    selecionado: f.tipo !== "facultativo", // Feriados oficiais vêm selecionados; facultativos deixam a critério da oficina
  }));
}

/**
 * Salva em lote no PocketBase os feriados selecionados pelo usuário,
 * realizando upsert (cria ou atualiza sem duplicar por data).
 */
export async function salvarFeriadosEmLote(ano, feriadosSelecionados) {
  const y = Number(ano);
  const existentes = await getFeriados(y);
  const mapExistentes = new Map(existentes.map((f) => [f.data, f]));

  let criados = 0;
  let atualizados = 0;

  for (const item of feriadosSelecionados) {
    const payload = {
      data: item.data,
      descricao: item.descricao,
      tipo: item.tipo || "nacional",
      ano: y,
      ativo: item.ativo !== false,
    };

    const existente = mapExistentes.get(item.data);
    if (existente) {
      await pb.collection("oficina_calendario_feriados").update(existente.id, payload);
      atualizados++;
    } else {
      await pb.collection("oficina_calendario_feriados").create(payload);
      criados++;
    }
  }

  return { criados, atualizados, total: feriadosSelecionados.length };
}
